// The picker over the case's identities, as the assign dialogs of the Unassigned faces and
// Unassigned voices tabs use it: one page of rows, narrowed server-side by a debounced search.
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AssignableIdentity } from '@/components/identities/AssignFacesDialog'
import { identityDisplayName } from '@/components/identities/identity-display-name'
import { useDebounced } from '@/hooks/use-debounced'
import { identitiesApi } from '@/services/api/identities'

/** The picker shows one page of identities; the search narrows it server-side. */
const PICKER_LIMIT = 50

/** The list rows the picker reads — only the naming parts matter here. */
interface IdentityRow {
  id: string
  name?: string | null
  nickname?: string | null
  unnamedSeq?: number | null
}

export interface IdentityPicker {
  query: string
  setQuery: (query: string) => void
  options: AssignableIdentity[]
  isLoading: boolean
}

/** `enabled`: the picker is open, so its rows are worth reading. */
export function useIdentityPicker(caseId: string, enabled: boolean): IdentityPicker {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query, 250).trim()
  const identities = useQuery({
    queryKey: ['case', caseId, 'identities', 'picker', debounced],
    queryFn: () =>
      identitiesApi.list(caseId, {
        limit: PICKER_LIMIT,
        q: debounced || undefined,
      }) as unknown as Promise<{ persons?: IdentityRow[] }>,
    enabled: enabled && Boolean(caseId),
  })
  return {
    query,
    setQuery,
    options: (identities.data?.persons ?? []).map(person => ({
      id: person.id,
      label: identityDisplayName(person, t),
    })),
    isLoading: identities.isLoading,
  }
}
